/* eslint-disable */
import React, { forwardRef, useEffect, useState, useRef, memo } from 'react';
import cls from 'classnames';
import PropTypes from 'prop-types';
import { Waypoint } from 'react-waypoint';
import createImageLoader from '@dcode/loader/createImageLoader';
import isString from '@dcode/is/string/string';
import css from './Img.scss';

const classes = {
	root: css.img,
	isBlurred: css.isBlurred,
	isLoaded: css.isLoaded,
};

const propTypes = {
	classes: PropTypes.objectOf(PropTypes.string),
	className: PropTypes.string,
	style: PropTypes.objectOf(PropTypes.string),
	width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	lazy: PropTypes.bool,
	alt: PropTypes.string.isRequired,
	dim: PropTypes.bool,
	loading: PropTypes.oneOf(['eager', 'auto', 'lazy']),
	placeholder: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.shape({
			preview: PropTypes.string,
			preSrc: PropTypes.string,
			trace: PropTypes.string,
			lqip: PropTypes.string,
		}).isRequired,
	]),
	srcSet: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.string),
		PropTypes.arrayOf(PropTypes.shape({
			src: PropTypes.string.isRequired,
		})),
	]),
	sizes: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.string),
		PropTypes.arrayOf(PropTypes.shape({
			media: PropTypes.string.isRequired,
			width: PropTypes.string.isRequired,
		})),
	]),
	src: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.shape({
			preview: PropTypes.string,
			preSrc: PropTypes.string,
			trace: PropTypes.string,
			lqip: PropTypes.string,
		}).isRequired,
	]),
	fallback: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.shape({
			preview: PropTypes.string,
			preSrc: PropTypes.string,
			trace: PropTypes.string,
			lqip: PropTypes.string,
		}).isRequired,
	]),
	onClick: PropTypes.func,
	onKeyPress: PropTypes.func,
	onLoadStart: PropTypes.func,
	onLoadError: PropTypes.func,
	onLoadProgress: PropTypes.func,
	onLoad: PropTypes.func,
	onLoadEnd: PropTypes.func,
};

const defaultProps = {
	classes,
	className: '',
	alt: '',
	placeholder: '',
	dim: false,
	loading: 'auto',
	waypoint: false,
	fallback: 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
	onLoadStart: Function.prototype,
	onLoadError: Function.prototype,
	onLoadProgress: Function.prototype,
	onLoad: Function.prototype,
	onLoadEnd: Function.prototype,
};

const getProp = (object, key) => (
	Object.prototype.hasOwnProperty.call(object, key) ? object[key] : null
);

const findProp = (object, keys) => (
	object[keys.find((key) => getProp(object, key))] || null
);

export const Img = forwardRef(({
	classes,
	className,
	width,
	height,
	alt,
	dim,
	loading,
	placeholder,
	waypoint,
	srcSet,
	sizes,
	src,
	fallback,
	onLoadStart,
	onLoadError,
	onLoadProgress,
	onLoad,
	onLoadEnd,
	...props
}, ref) => {
	const cancelLoader = useRef();
	const [initLoading, setInitLoading] = useState(!waypoint);
	const [loaded, setLoaded] = useState();
	const [srcset, setSrcset] = useState();
	const [source, setSource] = useState(findProp(placeholder, [
		'preview', // @see http://npmjs.com/package/sqip-loader
		'preSrc',  // @see http://npmjs.com/package/lqip-loader
		'trace',   // @see http://npmjs.com/package/image-trace-loader
		'lqip',    // @see http://npmjs.com/package/lqip.macro
	]));

	const getSizes = (value) => {
		if (!value) return 'auto';
		if (isString(value)) return value;
		return value.map((size, idx) => {
			if (isString(size)) return size;
			return `${size.media} ${size.width}`;
		}).join(', ');
	};

	const load = (assets, onComplete) => {
		return createImageLoader({
			assets,
			onComplete,
			onStart: onLoadStart,
			onProgress: onLoadProgress,
			onEmpty: () => console.warn('There\'s no source to load!'),
			onError: (file, xhr) => {
				console.error('Failed to load:', file);
				setSource(fallback);
				setLoaded(false);
				onLoadError(file, xhr);
			},
		});
	};

	const loadSrcSetImage = (list) => {
		const asset = list.find((_, index) =>
			(~~window.devicePixelRatio || 1) === (index + 1)
		);
		if (src !== asset) {
			setSource(asset);
		}
		return load(asset, (assets) => {
			setSrcset(list.map((img, idx) => {
				return `${isString(img) ? img : img.src} ${idx + 1}x`;
			}).join(', '));
			setLoaded(true);
			onLoadEnd(assets);
		});
	};

	const loadSrcSet = (list) => {
		if (isString(list)) {
			// @todo otimizar carregamento:
			// 1. transformar para array e chamar loadSrcSetImage?
			return setSrcset(list);
		}
		return loadSrcSetImage(list);
	};

	const loadSrc = (url) => {
		return load(url, (assets) => {
			const image = assets.list(0);
			onLoad(assets);
			setSource(image.src);
			setLoaded(true);
			onLoadEnd(assets);
		});
	};

	const onEnterCanvas = () => {
		setInitLoading(true);
		return onLeaveCanvas;
	};

	const onLeaveCanvas = () => {
		if (cancelLoader.current) {
			setInitLoading(false);
			cancelLoader.current();
		}
	};

	const renderImg = () => {
		return <img
			ref={ref}
			alt={alt}
			src={source}
			width={width}
			height={height}
			srcSet={srcset}
			sizes={srcset ? getSizes(sizes) : null}
			onLoadStart={() => setInitLoading(true)}
			loading={loading === 'lazy' || (width && height) ? 'lazy' : loading}
			crossOrigin={'anonymous'}
			className={cls(classes.root, className, {
				[classes.isBlurred]: dim,
				[classes.isLoaded]: loaded,
			})}
			{...props}
		/>
	};

	const renderLazyImg = () => {
		return <Waypoint
			onEnter={onEnterCanvas}
			onLeave={onLeaveCanvas}
			children={renderImg()}
		/>
	};

	useEffect(() => {
		if (initLoading) {
			if (srcSet) cancelLoader.current = loadSrcSet(srcSet);
			else if (src) cancelLoader.current = loadSrc(src);
			return cancelLoader.current;
		}
	}, [initLoading, src, srcSet]);

	return waypoint ? renderLazyImg() : renderImg();
});

Img.displayName = 'Img';
Img.propTypes = propTypes;
Img.defaultProps = defaultProps;
export default memo(Img);
